/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { Fragment, ReactNode } from 'react';

import {
  ButtonGroup,
  Grid,
  Heading,
  SkeletonBlock,
  Spacer,
  Text,
  Typography,
} from '@zapier/design-system';

import { isolateComponent } from '@zapier/style-encapsulation';

import { useSize } from '@zapier/upsells/src/hooks';
import { Size } from '@zapier/upsells/src/types';

const styles = {
  heading: {
    ...Typography.subHeader1Bold,
    marginBottom: 10,
  },
  imageContainer: (imageBackgroundColor: string) => ({
    alignItems: 'center',
    background: imageBackgroundColor,
    display: 'flex',
    justifyContent: 'center',
    paddingLeft: 30,
  }),
  subtitle: {
    ...Typography.smallPrint1,
  },
};

type Props = {
  bodyText: ReactNode | null;
  buttons: ReactNode[] | null;
  headingText: ReactNode | null;
  /**
   * Used for identifying views in tests.
   */
  id: string;
  image: ReactNode | null;
  imageBackgroundColor?: string;
  subtitleText: string | null | undefined;
};

const contentWrapper = (size: Size) =>
  css({
    padding: size === 'small' ? 0 : '30px 20px 30px 30px',
  });

export const UpgradeNudgeLayout = ({
  bodyText,
  buttons,
  headingText,
  id,
  image,
  imageBackgroundColor = 'none',
  subtitleText,
}: Props) => {
  const size = useSize();

  return (
    <div data-testid={id}>
      <Grid
        columns={size === 'small' ? 'auto' : '470px minmax(100px, 300px)'}
        gap={0}
      >
        <div css={contentWrapper(size)}>
          <Heading css={styles.subtitle}>{subtitleText}</Heading>

          <Spacer height={10} />

          {headingText ? (
            <Heading css={styles.heading}>{headingText}</Heading>
          ) : (
            <Fragment>
              <SkeletonBlock height={50} width="100%" />
              <Spacer height={10} />
            </Fragment>
          )}

          {bodyText ? (
            <Text color="gray60" tag="p" type="bodyText800">
              {bodyText}
            </Text>
          ) : (
            <SkeletonBlock height={65} width="100%" />
          )}

          <Spacer height={20} />

          {buttons ? (
            <ButtonGroup
              columnSize={size === 'small' ? '100%' : 'minmax(100px, 200px)'}
            >
              {buttons}
            </ButtonGroup>
          ) : (
            <SkeletonBlock height={50} width="100%" />
          )}
        </div>

        <div css={styles.imageContainer(imageBackgroundColor)}>
          {size !== 'small' && (image ? image : <SkeletonBlock width="100%" />)}
        </div>
      </Grid>
    </div>
  );
};
